import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { message } from 'components';

import { getExternalAppointmentSignature } from 'api/Covid19Controller';

import errorReporter from '@app/errorReporter';

import { useSearchParams } from 'hooks';
import { ExternalAuthContext } from 'contexts';

import { OfferDetailsType } from 'api/AppExternalController';
import { AppExternalController, RetsController } from 'api';

import getLockboxPhotoByRets from 'api/AppExternalController/getLockboxPhotoByRets';
import ShowingDetailsPage from './ShowingDetailsPage';
import { ShowingDetailsAppointmentType } from './types';

import Loading from '../MyBusinessPage/components/Loading';
import { ShowingDetailsErrorWrapper as errWrap } from './utils';

export type RetsShowingDetailsPageProps = {};

const noop = () => Promise.resolve();

const RetsShowingDetailsPage: React.FC<RetsShowingDetailsPageProps> = () => {
  const { appointmentId, retsId } = useParams<{
    appointmentId: string;
    retsId: string;
  }>();
  const queryParams = useSearchParams();
  const [appointment, setAppointment] =
    useState<ShowingDetailsAppointmentType | null>(null);

  const [loading, setLoading] = useState(true);

  const [lockboxPhoto, setLockBoxPhoto] = useState<string>('');

  const { auth } = useContext(ExternalAuthContext);

  const userType = 'rets';

  const [offerDetails, setOfferDetails] = useState<OfferDetailsType>({
    offerCount: 0,
    offerSettings: undefined,
    offerSubmissions: '',
  });

  const [signatureType, setSignatureType] = useState<'template' | 'pdf'>(
    'template',
  );

  const fetchAppointment = async () => {
    try {
      const fetchedAppointment = await RetsController.getAppointment(
        retsId,
        appointmentId,
      );

      setAppointment(fetchedAppointment as ShowingDetailsAppointmentType);

      if (
        fetchedAppointment &&
        ['SentriLockV2', 'MasterLock', 'Supra eKEY', 'Lock Box'].includes(
          fetchedAppointment.appointmentConfig?.access,
        )
      ) {
        const { data: photoURL } =
          (await errWrap(
            getLockboxPhotoByRets,
            retsId,
            'Error fetching lockbox photo',
          )) || {};
        if (photoURL) {
          setLockBoxPhoto(photoURL);
        }
      }

      if (auth?.hasFeature('OFFERS')) {
        const fetchedOfferDetails = await errWrap(
          AppExternalController.getRetsOfferDetails,
          retsId,
          'Error fetching offer details',
        );
        if (fetchedOfferDetails) setOfferDetails(fetchedOfferDetails);
      }

      if (
        !fetchedAppointment.covid19Overridden &&
        fetchedAppointment.covid19Signed
      ) {
        const fetchedAppointmentSignature = await errWrap(
          getExternalAppointmentSignature,
          {
            appointmentId: fetchedAppointment.id,
            dashboardInfo: {
              dashboardId: auth?.dashboardId || '',
              retsId,
            },
          },
          'Error fetching appointment covid signature',
        );
        setSignatureType(
          fetchedAppointmentSignature?.template ? 'template' : 'pdf',
        );
      }

      setLoading(false);
    } catch (err) {
      const msg = 'Error fetching appointment info';
      errorReporter.error(`${msg}: ${err}`);
      message.error(msg);
    }
  };

  useEffect(() => {
    fetchAppointment();
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (!(appointment && auth)) {
    return null;
  }

  return (
    <ShowingDetailsPage
      auth={{
        id: (auth.user as any).id,
        org: {
          country: auth.ownerOrg?.country || (auth.user as any).country,
        },
        hasFeature: auth.hasFeature,
      }}
      userType={userType}
      appointment={appointment}
      fetchAppointment={fetchAppointment}
      listing={auth.listing!}
      setFeedbackConfig={noop}
      fetchFeedback={noop}
      lockboxPhoto={lockboxPhoto}
      offerDetails={offerDetails}
      signatureType={signatureType}
      dashboardInfo={{
        dashboardId: '',
        retsId: auth.retsId || '',
      }}
      queryParams={queryParams}
    />
  );
};

export default RetsShowingDetailsPage;
